import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Clock, Check, X, Pencil, Search, ChevronDown, ChevronUp,
  DoorOpen, DoorClosed, Calendar, Plus, Trash2, RefreshCw, QrCode, Download,
  Bell, BellOff
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAYS_FR = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

export default function AdminSites() {
  const [secteurs, setSecteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editMargins, setEditMargins] = useState({
    margin_before_open: 30,
    margin_after_open: 5,
    margin_before_close: 1,
    margin_after_close: 30
  });
  const [expandedId, setExpandedId] = useState(null);
  
  // État pour les fermetures exceptionnelles
  const [closuresModalOpen, setClosuresModalOpen] = useState(false);
  const [selectedSiteForClosures, setSelectedSiteForClosures] = useState(null);
  const [closures, setClosures] = useState([]);
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [newClosureDay, setNewClosureDay] = useState('');
  const [newClosureMonth, setNewClosureMonth] = useState('');
  const [newClosureYear, setNewClosureYear] = useState(new Date().getFullYear().toString());
  const [newClosureLabel, setNewClosureLabel] = useState('');
  const [newClosureRecurring, setNewClosureRecurring] = useState(false);
  
  // État pour le QR Code
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedSiteForQr, setSelectedSiteForQr] = useState(null);
  const qrRef = useRef(null);
  
  // URL de base pour le scan (utiliser l'URL de production)
  const SCAN_BASE_URL = window.location.origin;
  
  const navigate = useNavigate();
  const isDark = document.documentElement.classList.contains('dark');
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchSecteurs();
  }, [navigate]);
  
  const fetchSecteurs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/secteurs-view`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecteurs(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des secteurs');
      if (error.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  };
  
  const startEdit = (secteur, e) => {
    e.stopPropagation();
    setEditingId(secteur.id);
    setEditMargins({
      margin_before_open: secteur.margin_before_open,
      margin_after_open: secteur.margin_after_open,
      margin_before_close: secteur.margin_before_close,
      margin_after_close: secteur.margin_after_close
    });
  };
  
  const cancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingId(null);
  };
  
  const saveMargins = async (secteurId, e) => {
    if (e) e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/secteurs/${secteurId}/margins`, editMargins, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marges mises à jour');
      setEditingId(null);
      fetchSecteurs();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  const toggleExpand = (id) => {
    if (editingId === id) return; // Don't collapse while editing
    setExpandedId(expandedId === id ? null : id);
  };
  
  // Fonctions pour les fermetures exceptionnelles
  const openClosuresModal = async (secteur, e) => {
    if (e) e.stopPropagation();
    setSelectedSiteForClosures(secteur);
    setClosuresModalOpen(true);
    await fetchClosures(secteur.id);
  };
  
  const fetchClosures = async (siteId) => {
    setLoadingClosures(true);
    try {
      const token = localStorage.getItem('token');
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${API}/sites/${siteId}/closures?year=${currentYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClosures(response.data.closures || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des fermetures');
      setClosures([]);
    } finally {
      setLoadingClosures(false);
    }
  };
  
  const toggleClosureEnabled = async (closure) => {
    const updatedClosures = closures.map(c => 
      c.date === closure.date ? { ...c, enabled: !c.enabled } : c
    );
    setClosures(updatedClosures);
    await saveClosures(updatedClosures);
  };
  
  const saveClosures = async (closuresToSave) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sites/${selectedSiteForClosures.id}/closures`, 
        { closures: closuresToSave },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fermetures mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };
  
  const addCustomClosure = async () => {
    if (!newClosureDay || !newClosureMonth || !newClosureYear || !newClosureLabel) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    // Valider la date
    const day = parseInt(newClosureDay);
    const month = parseInt(newClosureMonth);
    const year = parseInt(newClosureYear);
    
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2024) {
      toast.error('Date invalide');
      return;
    }
    
    // Formater la date en YYYY-MM-DD
    const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const newClosure = {
      date: formattedDate,
      label: newClosureLabel,
      enabled: true,
      recurring: newClosureRecurring,
      type: 'custom'
    };
    
    const updatedClosures = [...closures, newClosure];
    setClosures(updatedClosures);
    await saveClosures(updatedClosures);
    
    // Reset form
    setNewClosureDay('');
    setNewClosureMonth('');
    setNewClosureYear(new Date().getFullYear().toString());
    setNewClosureLabel('');
    setNewClosureRecurring(false);
  };
  
  const deleteClosure = async (closureDate) => {
    const updatedClosures = closures.filter(c => c.date !== closureDate);
    setClosures(updatedClosures);
    await saveClosures(updatedClosures);
  };
  
  const initHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentYear = new Date().getFullYear();
      await axios.post(`${API}/sites/${selectedSiteForClosures.id}/closures/init-holidays?year=${currentYear}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Jours fériés initialisés');
      await fetchClosures(selectedSiteForClosures.id);
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };
  
  // Fonctions pour le QR Code
  const openQrModal = (secteur, e) => {
    if (e) e.stopPropagation();
    setSelectedSiteForQr(secteur);
    setQrModalOpen(true);
  };
  
  const getQrUrl = () => {
    if (!selectedSiteForQr) return '';
    return `${SCAN_BASE_URL}?sector=${selectedSiteForQr.id}`;
  };
  
  const downloadQrPng = () => {
    if (!qrRef.current || !selectedSiteForQr) return;
    
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `QR_${selectedSiteForQr.nom.replace(/[^a-z0-9]/gi, '_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    
    toast.success('QR Code PNG téléchargé');
  };
  
  // Toggle alertes pour un secteur
  const toggleSectorAlerts = async (secteur, e) => {
    if (e) e.stopPropagation();
    
    const newValue = !secteur.alerts_enabled;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sites/${secteur.id}/alerts`, 
        { alerts_enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mettre à jour l'état local
      setSecteurs(prev => prev.map(s => 
        s.id === secteur.id ? { ...s, alerts_enabled: newValue } : s
      ));
      
      toast.success(newValue ? 'Alertes activées' : 'Alertes désactivées');
    } catch (error) {
      console.error('Error toggling alerts:', error);
      toast.error('Erreur lors de la modification');
    }
  };
  
  const filteredSecteurs = secteurs.filter(s => 
    s.nom.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5BA4CF]"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Secteurs
          </h1>
          <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
            Horaires synchronisés depuis Prysma • {secteurs.length} secteur{secteurs.length > 1 ? 's' : ''}
          </p>
        </div>
        
        {/* Search + Legend */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10"
              style={{
                background: isDark ? '#1f2937' : '#ffffff',
                borderColor: isDark ? '#374151' : '#e5e7eb'
              }}
            />
          </div>
          
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-green-500"></div>
              <span className="text-gray-600 dark:text-zinc-400">Ouverture</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-orange-500"></div>
              <span className="text-gray-600 dark:text-zinc-400">Fermeture</span>
            </div>
          </div>
        </div>
        
        {/* Cards Layout */}
        <div className="space-y-3">
          {filteredSecteurs.map((secteur, index) => {
            const isExpanded = expandedId === secteur.id;
            const isEditing = editingId === secteur.id;
            
            return (
              <motion.div
                key={secteur.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="rounded-xl border overflow-hidden"
                style={{ 
                  background: isDark ? '#1f2937' : '#ffffff',
                  borderColor: isDark ? '#374151' : '#e5e7eb'
                }}
              >
                {/* Card Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() => toggleExpand(secteur.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: '#5BA4CF20' }}
                    >
                      <MapPin className="h-5 w-5 text-[#5BA4CF]" />
                    </div>
                    
                    {/* Name + Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">
                          {secteur.nom}
                        </h3>
                        {/* Toggle Alertes ON/OFF */}
                        <button
                          onClick={(e) => toggleSectorAlerts(secteur, e)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                            secteur.alerts_enabled !== false
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          }`}
                          data-testid={`alerts-toggle-${secteur.id}`}
                          title={secteur.alerts_enabled !== false ? 'Alertes activées - Cliquez pour désactiver' : 'Alertes désactivées - Cliquez pour activer'}
                        >
                          {secteur.alerts_enabled !== false ? (
                            <>
                              <Bell className="h-3 w-3" />
                              <span className="hidden sm:inline">ON</span>
                            </>
                          ) : (
                            <>
                              <BellOff className="h-3 w-3" />
                              <span className="hidden sm:inline">OFF</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-zinc-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {secteur.total_slots} créneau{secteur.total_slots > 1 ? 'x' : ''}
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span 
                          className="flex items-center gap-1 cursor-pointer hover:text-[#5BA4CF] group"
                          onClick={(e) => startEdit(secteur, e)}
                        >
                          <DoorOpen className="h-3 w-3 text-green-500" />
                          -{secteur.margin_before_open}/+{secteur.margin_after_open}
                          <DoorClosed className="h-3 w-3 text-orange-500 ml-1" />
                          -{secteur.margin_before_close}/+{secteur.margin_after_close}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span 
                          className="flex items-center gap-1 cursor-pointer hover:text-[#5BA4CF] group"
                          onClick={(e) => openClosuresModal(secteur, e)}
                          data-testid={`closures-btn-${secteur.id}`}
                        >
                          <Calendar className="h-3 w-3 text-red-500" />
                          Fermetures
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span 
                          className="flex items-center gap-1 cursor-pointer hover:text-[#5BA4CF] group"
                          onClick={(e) => openQrModal(secteur, e)}
                          data-testid={`qr-code-btn-${secteur.id}`}
                        >
                          <QrCode className="h-3 w-3 text-purple-500" />
                          QR Code
                        </span>
                      </div>
                    </div>
                    
                    {/* Expand button */}
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Edit Margins Modal */}
                {isEditing && (
                  <div 
                    className="px-4 pb-4 pt-2 border-t"
                    style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Ouverture */}
                      <div 
                        className="p-4 rounded-lg"
                        style={{ background: isDark ? '#374151' : '#f0fdf4', border: '1px solid #22c55e40' }}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <DoorOpen className="h-4 w-4 text-green-500" />
                          <span className="font-semibold text-sm text-gray-900 dark:text-white">Ouverture</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-zinc-400 block mb-1">Avant (min)</label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={editMargins.margin_before_open}
                              onChange={(e) => setEditMargins({ ...editMargins, margin_before_open: parseInt(e.target.value) || 0 })}
                              className="w-full h-9 text-center text-sm rounded-lg border"
                              style={{
                                background: isDark ? '#1f2937' : '#ffffff',
                                borderColor: isDark ? '#4b5563' : '#e5e7eb',
                                color: isDark ? '#fff' : '#000'
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-zinc-400 block mb-1">Après (min)</label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={editMargins.margin_after_open}
                              onChange={(e) => setEditMargins({ ...editMargins, margin_after_open: parseInt(e.target.value) || 0 })}
                              className="w-full h-9 text-center text-sm rounded-lg border"
                              style={{
                                background: isDark ? '#1f2937' : '#ffffff',
                                borderColor: isDark ? '#4b5563' : '#e5e7eb',
                                color: isDark ? '#fff' : '#000'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Fermeture */}
                      <div 
                        className="p-4 rounded-lg"
                        style={{ background: isDark ? '#374151' : '#fff7ed', border: '1px solid #f9731640' }}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <DoorClosed className="h-4 w-4 text-orange-500" />
                          <span className="font-semibold text-sm text-gray-900 dark:text-white">Fermeture</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-zinc-400 block mb-1">Avant (min)</label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={editMargins.margin_before_close}
                              onChange={(e) => setEditMargins({ ...editMargins, margin_before_close: parseInt(e.target.value) || 0 })}
                              className="w-full h-9 text-center text-sm rounded-lg border"
                              style={{
                                background: isDark ? '#1f2937' : '#ffffff',
                                borderColor: isDark ? '#4b5563' : '#e5e7eb',
                                color: isDark ? '#fff' : '#000'
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-zinc-400 block mb-1">Après (min)</label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={editMargins.margin_after_close}
                              onChange={(e) => setEditMargins({ ...editMargins, margin_after_close: parseInt(e.target.value) || 0 })}
                              className="w-full h-9 text-center text-sm rounded-lg border"
                              style={{
                                background: isDark ? '#1f2937' : '#ffffff',
                                borderColor: isDark ? '#4b5563' : '#e5e7eb',
                                color: isDark ? '#fff' : '#000'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={cancelEdit}>
                        Annuler
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={(e) => saveMargins(secteur.id, e)}
                        className="bg-[#5BA4CF] hover:bg-[#4A93BE] text-white"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Expanded Content - Schedule */}
                {isExpanded && !isEditing && (
                  <div 
                    className="px-4 pb-4 pt-2 border-t"
                    style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                      {DAYS_ORDER.map(day => {
                        const daySchedule = secteur.schedule?.[day];
                        const isClosed = daySchedule?.closed;
                        const slots = daySchedule?.slots || [];
                        
                        return (
                          <div 
                            key={day}
                            className="p-3 rounded-lg"
                            style={{ 
                              background: isClosed 
                                ? (isDark ? '#374151' : '#f3f4f6')
                                : (isDark ? '#1f2937' : '#f9fafb'),
                              border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`
                            }}
                          >
                            <p className="text-xs font-semibold text-gray-600 dark:text-zinc-400 mb-2">
                              {DAYS_FR[day]}
                            </p>
                            {isClosed ? (
                              <p className="text-xs text-gray-400 dark:text-zinc-500 italic">Fermé</p>
                            ) : slots.length === 0 ? (
                              <p className="text-xs text-gray-400 dark:text-zinc-500">-</p>
                            ) : (
                              <div className="space-y-1.5">
                                {slots.map((slot, idx) => (
                                  <div 
                                    key={idx}
                                    className={`text-xs px-2 py-1 rounded font-medium text-center ${
                                      slot.type === 'ouverture'
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                    }`}
                                  >
                                    {slot.start}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        
        {filteredSecteurs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MapPin className="h-12 w-12 text-gray-300 dark:text-zinc-600 mb-4" />
            <p className="text-gray-500 dark:text-zinc-400 font-medium">Aucun secteur trouvé</p>
            {searchQuery && (
              <p className="text-sm text-gray-400 dark:text-zinc-500 mt-1">
                Essayez une autre recherche
              </p>
            )}
          </div>
        )}
        
        {/* Info */}
        <div className="mt-6 p-4 rounded-lg" style={{ background: isDark ? '#374151' : '#f3f4f6' }}>
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-[#5BA4CF] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Marges de validation</p>
              <p className="text-xs text-gray-600 dark:text-zinc-400 mt-1">
                Les marges définissent la fenêtre de temps pendant laquelle un scan est considéré comme valide.<br/>
                <span className="text-green-600 dark:text-green-400">Ouverture</span> : par défaut 30 min avant, 5 min après • 
                <span className="text-orange-600 dark:text-orange-400 ml-1">Fermeture</span> : par défaut 1 min avant, 30 min après
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal Fermetures Exceptionnelles */}
      <Dialog open={closuresModalOpen} onOpenChange={setClosuresModalOpen}>
        <DialogContent 
          className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          style={{ background: isDark ? '#1f2937' : '#ffffff' }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Calendar className="h-5 w-5 text-red-500" />
              Fermetures exceptionnelles
              {selectedSiteForClosures && (
                <span className="text-sm font-normal text-gray-500 dark:text-zinc-400">
                  — {selectedSiteForClosures.nom}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {loadingClosures ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-[#5BA4CF]" />
              </div>
            ) : (
              <>
                {/* Bouton initialiser jours fériés */}
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={initHolidays}
                    className="text-xs"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Réinitialiser les jours fériés
                  </Button>
                </div>
                
                {/* Jours fériés */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-2 flex items-center gap-2">
                    🇫🇷 Jours fériés
                  </h4>
                  <div className="space-y-1">
                    {closures.filter(c => c.type === 'holiday').map((closure) => (
                      <div 
                        key={closure.date}
                        className="flex items-center justify-between p-2 rounded-lg"
                        style={{ 
                          background: closure.enabled 
                            ? (isDark ? '#7f1d1d20' : '#fef2f2')
                            : (isDark ? '#374151' : '#f3f4f6')
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleClosureEnabled(closure)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              closure.enabled 
                                ? 'border-red-500 bg-red-500' 
                                : 'border-gray-300 dark:border-zinc-600'
                            }`}
                          >
                            {closure.enabled && <Check className="h-3 w-3 text-white" />}
                          </button>
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {closure.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-zinc-400 ml-2">
                              {new Date(closure.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                              {closure.recurring && (
                                <span className="ml-1 text-[#5BA4CF]">(chaque année)</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Fermetures personnalisées */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-2 flex items-center gap-2">
                    📅 Fermetures personnalisées
                  </h4>
                  <div className="space-y-1">
                    {closures.filter(c => c.type === 'custom').length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-zinc-400 italic py-2">
                        Aucune fermeture personnalisée
                      </p>
                    ) : (
                      closures.filter(c => c.type === 'custom').map((closure) => (
                        <div 
                          key={closure.date}
                          className="flex items-center justify-between p-2 rounded-lg"
                          style={{ 
                            background: closure.enabled 
                              ? (isDark ? '#7f1d1d20' : '#fef2f2')
                              : (isDark ? '#374151' : '#f3f4f6')
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleClosureEnabled(closure)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                closure.enabled 
                                  ? 'border-red-500 bg-red-500' 
                                  : 'border-gray-300 dark:border-zinc-600'
                              }`}
                            >
                              {closure.enabled && <Check className="h-3 w-3 text-white" />}
                            </button>
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {closure.label}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-zinc-400 ml-2">
                                {new Date(closure.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {closure.recurring && (
                                  <span className="ml-1 text-[#5BA4CF]">(chaque année)</span>
                                )}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => deleteClosure(closure.date)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                {/* Ajouter une fermeture */}
                <div 
                  className="p-4 rounded-lg border"
                  style={{ 
                    background: isDark ? '#374151' : '#f9fafb',
                    borderColor: isDark ? '#4b5563' : '#e5e7eb'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Ajouter une fermeture
                  </h4>
                  <div className="space-y-3">
                    <div onClick={(e) => e.stopPropagation()}>
                      <label className="text-xs text-gray-500 dark:text-zinc-400 mb-1 block">Libellé *</label>
                      <input
                        type="text"
                        placeholder="Ex: Fermeture annuelle"
                        value={newClosureLabel}
                        onChange={(e) => setNewClosureLabel(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        className="w-full h-9 px-3 rounded-md border text-sm"
                        style={{
                          background: isDark ? '#1f2937' : '#ffffff',
                          borderColor: isDark ? '#4b5563' : '#e5e7eb',
                          color: isDark ? '#ffffff' : '#1f2937'
                        }}
                      />
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <label className="text-xs text-gray-500 dark:text-zinc-400 mb-1 block">Date * (JJ/MM/AAAA)</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          placeholder="JJ"
                          min="1"
                          max="31"
                          value={newClosureDay}
                          onChange={(e) => setNewClosureDay(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 h-9 px-2 rounded-md border text-sm text-center"
                          style={{
                            background: isDark ? '#1f2937' : '#ffffff',
                            borderColor: isDark ? '#4b5563' : '#e5e7eb',
                            color: isDark ? '#ffffff' : '#1f2937'
                          }}
                        />
                        <span className="flex items-center text-gray-400">/</span>
                        <input
                          type="number"
                          placeholder="MM"
                          min="1"
                          max="12"
                          value={newClosureMonth}
                          onChange={(e) => setNewClosureMonth(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 h-9 px-2 rounded-md border text-sm text-center"
                          style={{
                            background: isDark ? '#1f2937' : '#ffffff',
                            borderColor: isDark ? '#4b5563' : '#e5e7eb',
                            color: isDark ? '#ffffff' : '#1f2937'
                          }}
                        />
                        <span className="flex items-center text-gray-400">/</span>
                        <input
                          type="number"
                          placeholder="AAAA"
                          min="2024"
                          max="2100"
                          value={newClosureYear}
                          onChange={(e) => setNewClosureYear(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-20 h-9 px-2 rounded-md border text-sm text-center"
                          style={{
                            background: isDark ? '#1f2937' : '#ffffff',
                            borderColor: isDark ? '#4b5563' : '#e5e7eb',
                            color: isDark ? '#ffffff' : '#1f2937'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: isDark ? '#4b5563' : '#e5e7eb' }}>
                    <label className="flex items-center gap-2 cursor-pointer select-none" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={newClosureRecurring}
                        onChange={(e) => setNewClosureRecurring(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-[#5BA4CF] focus:ring-[#5BA4CF]"
                      />
                      <span className="text-sm text-gray-700 dark:text-zinc-300">
                        Répéter chaque année
                      </span>
                    </label>
                    <Button
                      onClick={(e) => { e.stopPropagation(); addCustomClosure(); }}
                      className="bg-[#5BA4CF] hover:bg-[#4a93be] text-white"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal QR Code */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent 
          className="max-w-md"
          style={{ background: isDark ? '#1f2937' : '#ffffff' }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <QrCode className="h-5 w-5 text-purple-500" />
              QR Code
            </DialogTitle>
          </DialogHeader>
          
          {selectedSiteForQr && (
            <div className="space-y-4">
              {/* Nom du secteur */}
              <div className="text-center">
                <p className="font-semibold text-gray-900 dark:text-white text-lg">
                  {selectedSiteForQr.nom}
                </p>
                <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
                  Scannez ce QR Code pour accéder au secteur
                </p>
              </div>
              
              {/* QR Code - Noir sur fond blanc */}
              <div 
                ref={qrRef}
                className="flex justify-center p-6 rounded-xl"
                style={{ background: '#ffffff' }}
              >
                <QRCodeCanvas
                  value={getQrUrl()}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              {/* URL */}
              <div 
                className="p-3 rounded-lg text-xs font-mono break-all"
                style={{ 
                  background: isDark ? '#374151' : '#f3f4f6',
                  color: isDark ? '#9ca3af' : '#6b7280'
                }}
              >
                {getQrUrl()}
              </div>
              
              {/* Bouton de téléchargement PNG uniquement */}
              <Button
                onClick={downloadQrPng}
                className="w-full bg-[#5BA4CF] hover:bg-[#4a93be] text-white"
                data-testid="download-qr-png"
              >
                <Download className="h-4 w-4 mr-2" />
                Télécharger PNG
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
